import { multiFactor, User } from '@firebase/auth'
import {
  CODE_QUERY_PARAM,
  env,
  MODE_QUERY_PARAM,
  paths,
  REAUTHENTICATE_QUERY_PARAM,
  REAUTHENTICATION_STATUS_QUERY_PARAM,
  REAUTHENTICATION_VARIANT_QUERY_PARAM,
} from 'config'
import {
  Modes,
  ReauthenticationStatus,
  ReauthenticationVariant,
  SsoAuthProvider,
} from 'shared/types'

export const MICROSOFT_PROVIDER_ID = 'microsoft.com' as const

export const AUTH_PROVIDER_LABEL_KEYS: Record<SsoAuthProvider, string> = {
  [SsoAuthProvider.Microsoft]: 'sso.loginTypeMicrosoft',
}

export const getAvailableAuthProviders = (
  canAddSsoUser: boolean
): SsoAuthProvider[] => [...(canAddSsoUser ? [SsoAuthProvider.Microsoft] : [])]

export const OAUTH_PROVIDER_IDS = [MICROSOFT_PROVIDER_ID] as const

export type OAuthProviderId = typeof OAUTH_PROVIDER_IDS[number]

export const hasSignInProvider = (
  user: User | null | undefined,
  providerId: string
): boolean =>
  !!user?.providerData.some(provider => provider.providerId === providerId)

export const hasOAuthSignInProvider = (
  user: User | null | undefined
): boolean =>
  OAUTH_PROVIDER_IDS.some(providerId => hasSignInProvider(user, providerId))

export const isTwoFactorEnabled = (user: User | null | undefined): boolean =>
  !!user && multiFactor(user).enrolledFactors.length > 0

// 2FA is offered only for Blue Bolt accounts; Microsoft accounts are secured by MS
export const needsTwoFactorEnrollment = (user: User): boolean =>
  !env.hideTwoFactorAuth &&
  !hasOAuthSignInProvider(user) &&
  !isTwoFactorEnabled(user)

export const getReauthenticationPath = (variant: ReauthenticationVariant) =>
  `${paths.signIn}?${REAUTHENTICATE_QUERY_PARAM}=true&${REAUTHENTICATION_VARIANT_QUERY_PARAM}=${variant}`

export const getReauthenticationSuccessPath = (
  variant: ReauthenticationVariant
) => {
  const path =
    variant === ReauthenticationVariant.SECURITY
      ? paths.security
      : paths.deleteAccount
  return `${path}?${REAUTHENTICATION_STATUS_QUERY_PARAM}=${ReauthenticationStatus.SUCCESS}`
}

// Path of the page for a link from the "second factor added" email, or null for
// any other URL
export const getRevertSecondFactorLinkPath = ({
  pathname,
  search,
}: Pick<Location, 'pathname' | 'search'>) => {
  const params = new URLSearchParams(search)
  const isRevertSecondFactorLink =
    pathname !== paths.revertSecondFactor &&
    !!params.get(CODE_QUERY_PARAM) &&
    params.get(MODE_QUERY_PARAM) === Modes.RevertSecondFactorAddition

  return isRevertSecondFactorLink
    ? `${paths.revertSecondFactor}${search}`
    : null
}

export const isRevertSecondFactorPage = ({
  pathname,
}: Pick<Location, 'pathname'>) => pathname === paths.revertSecondFactor
